import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { pollService } from '../services/pollService';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBarChart3, FiCalendar, FiClock, FiUsers, FiHome } = FiIcons;

const PollResultsPage = () => {
  const { shareId } = useParams();
  const [poll, setPoll] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPoll();
  }, [shareId]);

  const loadPoll = async () => {
    try {
      setLoading(true);
      const pollData = await pollService.getPollByShareId(shareId);
      setPoll(pollData);
    } catch (error) {
      console.error('Error loading poll:', error);
    } finally {
      setLoading(false);
    }
  };

  const getChartData = () => {
    return poll.options.map(option => ({
      name: format(parseISO(option.date), 'dd.MM.', { locale: de }),
      fullDate: format(parseISO(option.date), 'dd.MM.yyyy', { locale: de }),
      time: option.time,
      stimmen: option.votes.length,
      total: option.votes.length
    }));
  };

  const getBestOptions = () => {
    const sortedOptions = [...poll.options].sort((a, b) => {
      return b.votes.length - a.votes.length;
    });
    return sortedOptions.slice(0, 3);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!poll) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Umfrage nicht gefunden
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Die angeforderte Umfrage existiert nicht oder ist nicht verfügbar.
          </p>
        </Card>
      </div>
    );
  }

  const chartData = getChartData();
  const bestOptions = getBestOptions();
  const totalVotes = poll.options.reduce((sum, option) => sum + option.votes.length, 0);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiBarChart3} className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Umfrage-Ergebnisse
          </h1>
          <h2 className="text-xl text-gray-600 dark:text-gray-400 mb-2">
            {poll.title}
          </h2>
          {poll.description && (
            <p className="text-gray-500 dark:text-gray-400">
              {poll.description}
            </p>
          )}
        </motion.div>

        {/* Stats Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
        >
          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiUsers} className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {totalVotes}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Gesamte Stimmen
            </p>
          </Card>

          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-success-100 dark:bg-success-900 rounded-lg flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiCalendar} className="w-6 h-6 text-success-600 dark:text-success-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {poll.options.length}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Terminvorschläge
            </p>
          </Card>

          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiBarChart3} className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {bestOptions[0]?.votes.length || 0}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Beste Option (Stimmen)
            </p>
          </Card>
        </motion.div>

        {/* Chart */}
        {chartData.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Abstimmungsergebnisse im Überblick
              </h2>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fill: 'currentColor' }}
                      className="text-gray-600 dark:text-gray-400"
                    />
                    <YAxis 
                      tick={{ fill: 'currentColor' }}
                      className="text-gray-600 dark:text-gray-400"
                    />
                    <Tooltip
                      labelFormatter={(label, payload) => {
                        const data = payload[0]?.payload;
                        return data ? `${data.fullDate} ${data.time}` : label;
                      }}
                      contentStyle={{
                        backgroundColor: 'var(--tooltip-bg)',
                        border: '1px solid var(--tooltip-border)',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="stimmen" fill="#22c55e" name="Stimmen" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Best Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              Beste Terminvorschläge
            </h2>
            <div className="space-y-4">
              {bestOptions.map((option, index) => {
                const voteCount = option.votes.length;
                const percentage = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;

                return (
                  <div
                    key={option.id}
                    className={`p-4 rounded-lg border-2 ${
                      index === 0 
                        ? 'border-success-200 bg-success-50 dark:border-success-800 dark:bg-success-900/20'
                        : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-4 ${
                          index === 0 
                            ? 'bg-success-100 dark:bg-success-900'
                            : 'bg-gray-200 dark:bg-gray-700'
                        }`}>
                          <span className={`text-sm font-bold ${
                            index === 0 
                              ? 'text-success-600 dark:text-success-400'
                              : 'text-gray-600 dark:text-gray-400'
                          }`}>
                            {index + 1}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center text-lg font-medium text-gray-900 dark:text-white mb-1">
                            <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-2" />
                            {format(parseISO(option.date), 'EEEE, dd. MMMM yyyy', { locale: de })}
                          </div>
                          <div className="flex items-center text-gray-600 dark:text-gray-400">
                            <SafeIcon icon={FiClock} className="w-4 h-4 mr-2" />
                            {option.time} ({option.duration} Min.)
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-gray-900 dark:text-white">
                          {percentage}%
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {voteCount} Stimme{voteCount !== 1 ? 'n' : ''}
                        </div>
                      </div>
                    </div>

                    {/* Voter List (if not anonymous) */}
                    {!poll.settings.isAnonymous && option.votes.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Abstimmungen:
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {option.votes.map((vote, voteIndex) => (
                            <span
                              key={voteIndex}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                            >
                              {vote.voter}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex justify-center"
        >
          <Link to="/">
            <Button>
              <SafeIcon icon={FiHome} className="w-4 h-4 mr-2" />
              Zur Startseite
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default PollResultsPage;